import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import * as MessageStore from './Message';
import { ActionDefinition, DefaultActionDefinition, createMessage } from '../models';
import { produce } from 'immer';
import { getTokens } from '../auth/service';
import { jwtDecode } from 'jwt-decode';
import { AccountInfo } from '@azure/msal-browser';
import * as AuthStore from '../store/Auth';
import * as FolderStore from '../store/Folder';
import { GlobalState } from '.';
import { getFolders } from 'services/Api.Folder.Service';

export enum ActionTypes {
    Start = '@@application/INIT/START',
    Complete = '@@application/INIT/COMPLETE',
    Failed = '@@application/INIT/FAILED',
    SetInitTask = '@@application/SET_INIT_TASK',
    SetPageTitle = '@@application/SET_PAGE_TITLE'
}

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface ApplicationState {
    initialized: ActionDefinition;
    pageTitle: string;
    initTask: string;
}

export const initialState: ApplicationState = {
    initialized: { ...DefaultActionDefinition, is: true },
    pageTitle: '',
    initTask: 'Logging In...'
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

export interface Start { type: ActionTypes.Start }
export interface Complete { type: ActionTypes.Complete }
export interface Failed { type: ActionTypes.Failed }
export interface SetInitTask { type: ActionTypes.SetInitTask, task: string }
export interface SetPageTitle { type: ActionTypes.SetPageTitle, title: string }

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type Actions = Start | Complete | Failed | SetInitTask | SetPageTitle;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    start: (account: AccountInfo): AppThunkAction<Actions | MessageStore.Actions | AuthStore.Actions | FolderStore.Actions> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.Start });

        let accessToken = null;
        try {
            const token = await getTokens(account) as any;
            console.log('token response', token);
            accessToken = token?.accessToken;
            if (!accessToken) {
                dispatch({ type: ActionTypes.SetInitTask, task: 'Error obtaining an access token' });
            }
        } catch (error) {
            const err = error as any;
            console.error('An error occurred obtaining an access token', error);
            dispatch(MessageStore.actionCreators.show(createMessage('An error occurred logging in', err.message, err)));
        }

        if (!accessToken) {
            return;
        }

        // AUTHORIZE USER (user must have "Admin" role)
        const token = jwtDecode(accessToken) as { roles: string[] };
        const authorized = token && token.roles && token.roles.includes('Admin');
        if (!authorized) {
            const err = new Error("User is not authorized. User must be a part of the 'Requestor' role.");
            dispatch(AuthStore.actionCreators.loginFailed(err));
            return;
        }
        dispatch(AuthStore.actionCreators.loginComplete(account.username));

        dispatch({ type: ActionTypes.SetInitTask, task: 'Loading Folders...' });
        try {
            const folders = (await getFolders(accessToken));
            dispatch({ type: FolderStore.ActionTypes.MoveFolderComplete, folders });
        } catch (error) {
            const err = error as any;
            dispatch(MessageStore.actionCreators.show(createMessage('Error loading folders', err.message, err)));
        }

        dispatch({ type: ActionTypes.SetInitTask, task: 'Wrapping Up...' });
        dispatch({ type: ActionTypes.Complete });
    }
};

// ----------------
// SELECTORS - These are functions that, given the application state, will access a particular piece of information
// from the current store

export const selectors = {
    getInitialized: (state: GlobalState) => state.application.initialized,
    getInitTask: (state: GlobalState) => state.application.initTask,
    getPageTitle: (state: GlobalState) => state.application.pageTitle
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<ApplicationState> = (state: ApplicationState | undefined, incomingAction: Action): ApplicationState => {
    if (state === undefined) {
        return initialState;
    }

    const action = incomingAction as Actions;
    switch (action.type) {
        case ActionTypes.Start: {
            return produce(state, draft => {
                draft.initialized.is = true;
            });
        }
        case ActionTypes.Complete: {
            return produce(state, draft => {
                draft.initialized.is = false;
            });
        }
        case ActionTypes.SetInitTask: {
            return produce(state, draft => {
                draft.initTask = action.task;
            });
        }
        case ActionTypes.SetPageTitle: {
            return produce(state, draft => {
                draft.pageTitle = action.title;
            });
        }
        default: {
            return state;
        }
    }
};
